import { inject, Injectable } from "@angular/core";
import { ConfigService } from "./config.service";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, catchError, filter, map, switchMap } from "rxjs";
import { AuthService } from "../auth/auth.service";
import { UserService } from "./user.service";
import { NotificationService } from "./notification.service";
import { User } from "../models/user.model";

@Injectable({
    providedIn: 'root'
})
export class I18nService {
    private readonly STORAGE_KEY = 'languageCode';
    private readonly DEFAULT_LANGUAGE = 'en-GB';

    private readonly configService = inject(ConfigService);
    private readonly translateService = inject(TranslateService);
    private readonly authService = inject(AuthService);
    private readonly userService = inject(UserService);
    private readonly notificationService = inject(NotificationService);

    public language$ = new BehaviorSubject<string>(this.translateService.getCurrentLang());

    private supportedLangs : string[] = [];
    private isMockLogin = false;

    constructor() {
        this.initializeI18n();
        this.translateService.onLangChange.subscribe(event => {
            this.language$.next(event.lang);
        });

        this.authService.isMockLogin$.subscribe(isMockLogin => this.isMockLogin = isMockLogin);

        // Every login path (password, OTP, login-as) and every page reload ends in
        // AuthService.fetchCurrentUser(), so this is the single place the org default is applied.
        this.authService.currentUser$.pipe(
            filter((user): user is User => !!user),
            // config.getLanguages() is shareReplay'd; waiting on it avoids racing the bootstrap request.
            switchMap(user => this.configService.getLanguages().pipe(
                map(langs => ({ user, supportedLangs: langs.map(lang => lang.languageCode) })),
                catchError(() => EMPTY)
            ))
        ).subscribe(({ user, supportedLangs }) => this.applyForUser(user, supportedLangs));
    }

    public initializeI18n(): void {
        this.configService.getLanguages().subscribe(langs => {
            this.supportedLangs = langs.map(lang => lang.languageCode);
            this.translateService.addLangs(this.supportedLangs);
            this.translateService.setFallbackLang(this.DEFAULT_LANGUAGE);

            const storedLang = localStorage.getItem(this.STORAGE_KEY);
            if (storedLang && this.supportedLangs.includes(storedLang)) {
                this.translateService.use(storedLang);
            } else {
                this.translateService.use(this.DEFAULT_LANGUAGE);
            }
        });

    }

    public get language() {
        return this.translateService.getCurrentLang();
    }

    public set language(langCode: string) {
        if (!this.supportedLangs.includes(langCode)) {
            return;
        }

        this.applyLanguage(langCode);

        // Login screen: nothing to save to. Login-as: never write the impersonated user's preference.
        if (!this.authService.getAccessToken() || this.isMockLogin) {
            return;
        }

        this.userService.updateMyLanguage(langCode).subscribe({
            // Re-fetch so the cached current user carries the new preference; otherwise a later
            // fetchCurrentUser() would re-apply the stale one.
            next: () => this.authService.fetchCurrentUser().subscribe(),
            error: () => this.notificationService.error(
                this.translateService.instant('pages.userProfile.appLanguage.saveError')
            )
        });
    }

    /** user's own preference -> organization default -> en-GB */
    private applyForUser(user: User, supportedLangs: string[]): void {
        this.supportedLangs = supportedLangs;
        this.translateService.addLangs(supportedLangs);

        const candidates = [user.languageCode, user.organizationDefaultLanguageCode];
        const langCode = candidates.find((code): code is string => !!code && supportedLangs.includes(code))
            ?? this.DEFAULT_LANGUAGE;

        this.applyLanguage(langCode);
    }

    /** Switches the UI language and caches it so the next cold start (and the login screen) paints it at once. */
    private applyLanguage(langCode: string): void {
        this.translateService.use(langCode);
        localStorage.setItem(this.STORAGE_KEY, langCode);
    }
}
